<template>
  <div class="fct">
    <div v-for="comment in comments" :key="comment.id" class="fct-comment">
      <div class="fct-row">
        <div class="fct-avatar" :style="avatarStyle(comment.author)">
          {{ initials(comment.author?.name) }}
        </div>
        <div class="fct-body">
          <div class="fct-bubble">
            <span class="fct-name">{{ comment.author?.name || $t('post.anonymous')  }}</span>
            <div class="fct-text">
              <template v-if="comment.replyTo && comment.replyTo.name">
                <span class="fct-mention">@{{ comment.replyTo.name }}</span>
              </template>
              {{ comment.text }}
            </div>
            <FeedAttachmentList
              v-if="comment.attachments && comment.attachments.length"
              :attachments="comment.attachments"
            />
          </div>

          <div class="fct-actions">
            <div class="fct-reaction-control">
              <FeedReactionButton
                :reaction-type="myReaction(comment)"
                :count="reactionCount(comment)"
                :options="REACTION_OPTIONS"
                @react="react(comment, null, $event)"
              />
              <div v-if="reactionsOf(comment).length" class="fct-reactions">
                <f7-link
                  v-for="r in reactionsOf(comment)"
                  :key="r.reactionType"
                  class="fct-reaction-chip"
                  :class="{ 'fct-reaction-chip--active': myReaction(comment) === r.reactionType }"
                  @click="react(comment, null, r.reactionType)"
                >
                  <span class="fct-reaction-emoji">{{ reactionEmoji(r.reactionType) }}</span>
                  <span class="fct-reaction-count">{{ r.count }}</span>
                </f7-link>
              </div>
            </div>
            <f7-link class="fct-action" @click="toggleReplyForm(comment, null)">{{$t('post.reply')}}</f7-link>
            <span class="fct-time">{{ comment.timeAgo || '' }}</span>
          </div>

          

          <div
            v-if="replyingTo && replyingTo.commentId === comment.id"
            class="fct-reply-composer"
          >
            <FeedCommentComposer
              :key="'reply-' + replyingTo.key"
              :mention-name="replyMentionName"
              :placeholder="`${$t('post.replyTo')} ${replyMentionName || '...'}...`"
              :show-cancel="true"
              :auto-focus="true"
              @submit="submitReply"
              @cancel="replyingTo = null"
            />
          </div>
        </div>
      </div>

      <div v-if="commentReplies(comment).length" class="fct-replies">
        <div v-for="reply in commentReplies(comment)" :key="reply.id" class="fct-row fct-row--reply">
          <div class="fct-avatar fct-avatar--sm" :style="avatarStyle(reply.author)">
            {{ initials(reply.author?.name) }}
          </div>
          <div class="fct-body">
            <div class="fct-bubble">
              <span class="fct-name">{{ reply.author?.name || $t('post.anonymous')  }}</span>
              <div class="fct-text">
                <template v-if="reply.replyTo && reply.replyTo.name">
                  <span class="fct-mention">@{{ reply.replyTo.name }}</span>
                </template>
                {{ reply.text }}
              </div>
              <FeedAttachmentList
                v-if="reply.attachments && reply.attachments.length"
                :attachments="reply.attachments"
              />
            </div>

            <div class="fct-actions">
              <div class="fct-reaction-control">
                <FeedReactionButton
                  :reaction-type="myReaction(reply)"
                  :count="reactionCount(reply)"
                  :options="REACTION_OPTIONS"
                  @react="react(comment, reply, $event)"
                />
                <div v-if="reactionsOf(reply).length" class="fct-reactions">
                  <f7-link
                    v-for="r in reactionsOf(reply)"
                    :key="r.reactionType"
                    class="fct-reaction-chip"
                    :class="{ 'fct-reaction-chip--active': myReaction(reply) === r.reactionType }"
                    @click="react(comment, reply, r.reactionType)"
                  >
                    <span class="fct-reaction-emoji">{{ reactionEmoji(r.reactionType) }}</span>
                    <span class="fct-reaction-count">{{ r.count }}</span>
                  </f7-link>
                </div>
              </div>
              <f7-link class="fct-action" @click="toggleReplyForm(comment, reply)">{{$t('post.reply')}}</f7-link>
              <span class="fct-time">{{ reply.timeAgo || '' }}</span>
            </div>

          </div>
        </div>
      </div>

      <f7-link
        v-if="totalReplies(comment) > commentReplies(comment).length"
        class="fct-load-more"
        @click="$emit('load-more-replies', comment.id)"
      >
        {{$t('post.viewMoreReplies', { count: totalReplies(comment) - commentReplies(comment).length })}}
      </f7-link>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import i18n from '../../js/i18n.js'
import { REACTION_OPTIONS, reactionEmoji } from '../../js/feedReactions.js'

const { t } = i18n.global
import FeedCommentComposer from './FeedCommentComposer.vue'
import FeedReactionButton from './FeedReactionButton.vue'
import FeedAttachmentList from './FeedAttachmentList.vue'

const props = defineProps({
  comments: { type: Array, default: () => [] },
  currentUserId: { type: String, default: '' },
})

const emit = defineEmits(['react-comment', 'reply', 'load-more-replies'])

const replyingTo = ref(null)

const replyMentionName = computed(() => {
  if (!replyingTo.value) return ''
  const comment = props.comments.find((c) => c.id === replyingTo.value.commentId)
  if (replyingTo.value.replyId) {
    const reply = commentReplies(comment)?.find((r) => r.id === replyingTo.value.replyId)
    return reply?.author?.name || ''
  }
  return comment?.author?.name || ''
})

function initials(name) {
  if (!name) return '?'
  return name.split(' ').map((w) => w[0]).join('').substring(0, 2).toUpperCase()
}

function avatarStyle(author) {
  const colors = ['#1A6B42', '#3B4FCA', '#C9A84C', '#C0440A', '#6B46C1']
  const hash = (author?.name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0)
  const bg = colors[hash % colors.length]
  return { background: bg, color: '#fff' }
}

function commentReplies(comment) {
  return comment?.replies || comment?.sub_comments || []
}

function totalReplies(comment) {
  return comment?.repliesCount ?? comment?.replies_count ?? commentReplies(comment).length
}

function reactionsOf(target) {
  if (!target) return []
  if (Array.isArray(target.reactions) && target.reactions.length) {
    return target.reactions.filter((r) => (r.count || 0) > 0)
  }
  if (target.likesCount) {
    return [{ reactionType: 'LIKE', count: target.likesCount }]
  }
  return []
}

function reactionCount(target) {
  return reactionsOf(target).reduce((sum, r) => sum + (r.count || 0), 0)
}

function myReaction(target) {
  if (!target || !props.currentUserId) return null
  if (Array.isArray(target.reactions) && target.reactions.length) {
    const r = target.reactions.find((x) => (x.users || []).includes(props.currentUserId))
    return r ? r.reactionType : null
  }
  return target.userLiked ? 'LIKE' : null
}

function toggleReplyForm(comment, reply) {
  const key = reply ? `${comment.id}-${reply.id}` : `${comment.id}`
  if (replyingTo.value && replyingTo.value.key === key) {
    replyingTo.value = null
    return
  }
  replyingTo.value = { key, commentId: comment.id, replyId: reply ? reply.id : null }
}

function react(comment, reply, reactionType) {
  emit('react-comment', {
    commentId: comment.id,
    replyId: reply ? reply.id : null,
    reactionType,
  })
}

function submitReply(payload) {
  if (!replyingTo.value) return
  const { commentId, replyId } = replyingTo.value
  const comment = props.comments.find((c) => c.id === commentId)
  const target = replyId
    ? commentReplies(comment)?.find((r) => r.id === replyId)
    : comment
  emit('reply', {
    commentId,
    replyId,
    text: payload.text,
    attachments: payload.attachments,
    replyTo: target && target.author ? { id: target.id, name: target.author.name } : null,
  })
  replyingTo.value = null
}
</script>

<style scoped>
.fct { padding: 0 4px; }
.fct-comment { margin-bottom: 10px; }
.fct-row {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}
.fct-row--reply {
  margin-left: 34px;
  margin-top: 6px;
}
.fct-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 500;
  flex-shrink: 0;
}
.fct-avatar--sm { width: 22px; height: 22px; font-size: 8px; }
.fct-body { flex: 1; min-width: 0; }
.fct-bubble {
  background: var(--f7-list-bg-color, #f5f5f5);
  border-radius: 10px;
  padding: 6px 10px;
  font-size: 11px;
  line-height: 1.4;
}
.fct-name { font-weight: 500; margin-right: 2px; }
.fct-text {
  display: inline;
  word-break: break-word;
  white-space: pre-wrap;
}
.fct-mention {
  color: var(--avocash-green, #1A6B42);
  font-weight: 500;
}
.fct-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 8px;
  row-gap: 3px;
  padding: 3px 4px 0;
}
.fct-reaction-control {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}
.fct-action {
  font-size: 10px;
  color: var(--f7-on-surface-variant, #8e8e93);
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 0;
}
.fct-time {
  font-size: 10px;
  color: var(--f7-on-surface-variant, #8e8e93);
  white-space: nowrap;
}
.fct-reactions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
}
.fct-reaction-chip {
  display: inline-flex;
  align-items: center;
  gap: 1px;
  /* background: var(--f7-list-bg-color, #f0f0f0);
  border: 0.5px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
  border-radius: 12px; */
  padding: 1px 2px;
  font-size: 8px;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
}
.fct-reaction-chip--active {
  background: #EAF2EB;
  border-color: var(--avocash-green, #1A6B42);
}
.fct-reaction-count { color: var(--f7-on-surface-variant, #8e8e93); }
.fct-reaction-chip--active .fct-reaction-count { color: var(--avocash-green, #1A6B42); font-weight: 500; }
.fct-replies { margin-top: 4px; }
.fct-load-more {
  font-size: 11px;
  color: var(--f7-on-surface-variant, #8e8e93);
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 4px 0;
}
.fct-reply-composer { margin-top: 6px; }
</style>
